import { API_BASE_URL } from "../config/api";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  applyThemePreference,
  getStoredThemePreference,
  THEME_OPTIONS
} from "../utils/theme";

export default function AccountsProfile() {
  const accountsName = localStorage.getItem("accountsName") || "Accounts";
  const accountsEmail = localStorage.getItem("accountsEmail") || "";
  const accountsLookup = accountsEmail || accountsName;
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({
    name: accountsName,
    email: accountsEmail,
    role: "accounts"
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: ""
  });
  const [themePreference, setThemePreference] = useState(() => getStoredThemePreference());

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profileUrl = accountsLookup
          ? API_BASE_URL + `/api/accounts/profile/${encodeURIComponent(accountsLookup)}`
          : API_BASE_URL + "/api/accounts/profile";
        const res = await axios.get(profileUrl);
        setProfileForm({
          name: res.data.name || "",
          email: res.data.email || accountsEmail,
          role: res.data.role || "accounts"
        });
      } catch (error) {
        setProfileForm({
          name: accountsName,
          email: accountsEmail,
          role: "accounts"
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [accountsEmail, accountsLookup, accountsName]);

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      const passwordUrl = accountsLookup
        ? API_BASE_URL + `/api/accounts/profile/${encodeURIComponent(accountsLookup)}/password`
        : API_BASE_URL + "/api/accounts/profile/password";
      await axios.put(passwordUrl, passwordForm);
      setPasswordForm({ currentPassword: "", newPassword: "" });
      alert("Password updated successfully");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update password");
    }
  };

  const updateThemePreference = (value) => {
    setThemePreference(value);
    applyThemePreference(value);
  };

  const content = loading ? (
    <div className="p-6">Loading profile...</div>
  ) : (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="mt-1 text-sm text-gray-500">
          View your details here. Only admin can edit personal information.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Profile Details</h2>
          <div className="flex justify-center">
            <div className="flex h-28 w-28 items-center justify-center rounded-full border border-gray-200 bg-amber-100 text-3xl font-bold text-amber-900 shadow-sm">
              {(profileForm.name || "A").charAt(0).toUpperCase()}
            </div>
          </div>
          {[
            ["name", "Name"],
            ["email", "Email"],
            ["role", "Role"]
          ].map(([key, label]) => (
            <div key={key}>
              <label className="mb-1 block text-sm font-semibold text-gray-700">{label}</label>
              <input
                value={profileForm[key]}
                readOnly
                className="w-full rounded-xl border border-gray-300 bg-gray-100 px-4 py-3 text-gray-500"
              />
            </div>
          ))}
          <div className="rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Contact admin if any of these details need to be changed.
          </div>
        </div>

        <form onSubmit={changePassword} className="space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">Current Password</label>
            <input
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm((current) => ({ ...current, currentPassword: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 px-4 py-3"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-gray-700">New Password</label>
            <input
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm((current) => ({ ...current, newPassword: e.target.value }))}
              className="w-full rounded-xl border border-gray-300 px-4 py-3"
            />
          </div>
          <button className="rounded-xl bg-gray-900 px-5 py-3 text-white hover:bg-black">
            Update Password
          </button>
        </form>

        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Appearance</h2>
          <p className="text-sm text-gray-500">
            Choose how the app should look on this device.
          </p>
          <div className="space-y-3">
            {THEME_OPTIONS.map((option) => {
              const isActive = themePreference === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateThemePreference(option.value)}
                  className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
                    isActive
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="font-medium">{option.label}</span>
                  <span className="text-xs uppercase tracking-wide">
                    {isActive ? "Selected" : "Choose"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return content;
}
